import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Code2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';
import { navigationLinks } from '../../data/mock';

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href) => {
    setMobileOpen(false);
    if (href.startsWith('#')) {
      if (location.pathname === '/') {
        const el = document.querySelector(href);
        el?.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/' + href);
      }
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${
        scrolled
          ? 'bg-white/80 backdrop-blur-xl border-b border-stone-200/60 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center transition-[transform] duration-200 group-hover:scale-105">
              <Code2 className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold text-stone-900 tracking-tight font-sans">
              PyAuditSystems
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {navigationLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href)}
                className="text-sm font-medium text-stone-500 hover:text-stone-900 transition-colors duration-200"
              >
                {link.label}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Button
              variant="ghost"
              className="text-sm font-medium text-stone-600 hover:text-stone-900"
              onClick={() => handleNavClick('#pricing')}
            >
              View Plans
            </Button>
            <Link to="/custom-quote">
              <Button className="bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium px-5">
                Custom Quote
              </Button>
            </Link>
          </div>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon">
                <Menu className="w-5 h-5 text-stone-700" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-white">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <SheetDescription className="sr-only">
                Main navigation links
              </SheetDescription>
              <div className="flex flex-col gap-1 mt-8">
                {navigationLinks.map((link) => (
                  <button
                    key={link.label}
                    onClick={() => handleNavClick(link.href)}
                    className="text-left text-base font-medium text-stone-700 hover:text-amber-600 hover:bg-amber-50 transition-colors duration-200 py-3 px-3 rounded-lg"
                  >
                    {link.label}
                  </button>
                ))}
                <div className="mt-6 pt-6 border-t border-stone-200 space-y-3">
                  <Button
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                    onClick={() => handleNavClick('#pricing')}
                  >
                    View Plans
                  </Button>
                  <Link to="/custom-quote" onClick={() => setMobileOpen(false)}>
                    <Button
                      variant="outline"
                      className="w-full border-stone-300 text-stone-700"
                    >
                      Custom Quote
                    </Button>
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
